import React from 'react';
import { Radio } from 'lucide-react';

/**
 * PanelStatus — small "LIVE / UPDATING / STALE" pill shown in the header of
 * a data panel. Driven by the panel's last-updated timestamp; color tiers:
 *   fresh    (<30s)  → cyan + pulse
 *   updating       → amber spinner
 *   delayed  (<5m)  → yellow
 *   stale    (>5m)  → red
 */
function ageSeconds(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

export default function PanelStatus({ lastUpdated, loading, testId = 'panel-status' }) {
  if (loading) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase font-mono px-2 py-0.5 border border-hold text-hold"
      >
        <Radio size={8} className="animate-pulse" /> updating
      </span>
    );
  }
  const age = ageSeconds(lastUpdated);
  if (age === null) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase font-mono px-2 py-0.5 border border-[var(--txt-muted)] text-[var(--txt-muted)]"
      >
        ○ offline
      </span>
    );
  }
  let state, cls;
  if (age <= 30) {
    state = 'live';
    cls = 'border-cyan-sys text-cyan-sys';
  } else if (age <= 120) {
    state = 'recent';
    cls = 'border-buy text-buy';
  } else if (age <= 300) {
    state = 'delayed';
    cls = 'border-hold text-hold';
  } else {
    state = 'stale';
    cls = 'border-sell text-sell';
  }
  const label = age < 60 ? `${age}s` : age < 3600 ? `${Math.floor(age / 60)}m` : `${Math.floor(age / 3600)}h`;
  return (
    <span
      data-testid={testId}
      title={`last update ${label} ago`}
      className={`inline-flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase font-mono px-2 py-0.5 border ${cls}`}
    >
      <Radio size={8} className={state === 'live' ? 'animate-pulse' : ''} /> {state} <span className="tabular">· {label}</span>
    </span>
  );
}
